import React from 'react';
import '@rainbow-me/rainbowkit/styles.css';
import { RainbowKitProvider, connectorsForWallets } from '@rainbow-me/rainbowkit';
import {
  metaMaskWallet,
  coinbaseWallet,
  walletConnectWallet,
  injectedWallet,
  rainbowWallet,
} from '@rainbow-me/rainbowkit/wallets';
import { createConfig, WagmiProvider, http, createStorage } from 'wagmi';
import { mainnet, base } from 'wagmi/chains';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// WalletConnect Cloud Project ID — required for cross-device QR/mobile deep-links.
// Get one free at https://cloud.reown.com (takes 2 minutes).
const RAW_WC_ID = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || '';
const WC_PROJECT_ID = RAW_WC_ID.replace(/-/g, '').trim();
const hasValidWC = WC_PROJECT_ID && WC_PROJECT_ID.length >= 20;

// App metadata shown to the user inside their wallet app during connection.
// Pinned to the production-ish preview URL so MetaMask/Coinbase show a consistent origin.
const APP_URL = (typeof window !== 'undefined' && window.location.origin)
  || 'https://memory-rfq-market.preview.emergentagent.com';
const APP_ICON = `${APP_URL}/favicon.ico`;

// Build wallet list.
const popularWallets = [
  metaMaskWallet,
  coinbaseWallet,
  injectedWallet,
];
if (hasValidWC) {
  popularWallets.push(walletConnectWallet, rainbowWallet);
}

const connectors = connectorsForWallets(
  [
    { groupName: 'Popular', wallets: popularWallets },
  ],
  {
    appName: 'ServerMemoryRFQ',
    appDescription: 'B2B server memory marketplace — Web3 AI agent commerce',
    appUrl: APP_URL,
    appIcon: APP_ICON,
    projectId: hasValidWC ? WC_PROJECT_ID : 'DISABLED',
  }
);

// Persistent storage — keeps the WC session alive across tab suspends (critical for iOS Safari)
const safeStorage = typeof window !== 'undefined' ? window.localStorage : undefined;

const config = createConfig({
  connectors,
  chains: [base, mainnet],
  transports: {
    [base.id]: http(),
    [mainnet.id]: http(),
  },
  ssr: false,
  storage: safeStorage ? createStorage({ storage: safeStorage }) : undefined,
  // Auto-reconnect on mount — wagmi default is true, we keep it explicit here
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

export function Web3Provider({ children }) {
  return (
    <WagmiProvider config={config} reconnectOnMount>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider>
          {children}
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

export { config, hasValidWC };
